import classNames from 'classnames';
import React, {ChangeEvent, ReactElement, useCallback, useMemo, useState} from 'react';

import Col from '../../../common/Col';
import Row from '../../../common/Row';
import {Supplier} from '../../../query/graphql';
import Button from '../buttons/Button';
import Checkbox from '../Checkbox';
import GenericCard from '../GenericCard';
import SearchInput from '../SearchInput';

const COLLAPSED_COUNT = 5;

type Props = {
  supplierData: {
    supplier: Pick<Supplier, 'slug' | 'name'>;
    selected: boolean;
  }[];
  onChange: (selected: string[]) => void;
  className?: string;
};

const SearchBySupplierBox = ({supplierData, onChange, className}: Props): ReactElement => {
  const [supplierFilter, setSupplierFilter] = useState<string>();
  const [showAll, setShowAll] = useState<boolean>(false);

  const selectedCount = useMemo(() => supplierData.filter(data => data.selected).length, [supplierData]);

  const filteredData = useMemo(
    () =>
      supplierFilter
        ? supplierData.filter(data => data.supplier.name?.toLowerCase().includes(supplierFilter.toLowerCase()))
        : supplierData,
    [supplierData, supplierFilter]
  );

  const shownData = useMemo(
    () => (showAll ? filteredData : filteredData.slice(0, COLLAPSED_COUNT)),
    [filteredData, showAll]
  );

  const toggleShowAll = useCallback(() => setShowAll(prevState => !prevState), []);

  const handleChangeSupplierFilter = useCallback((value: string) => {
    setSupplierFilter(value);
  }, []);

  const handleChangeSelectedSuppliers = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const currentSuppliers = supplierData.filter(data => data.selected).map(data => data.supplier.slug);
      const suppliers = e.currentTarget.checked
        ? [...currentSuppliers, e.currentTarget.value]
        : currentSuppliers.filter(s => s !== e.currentTarget.value);
      onChange(suppliers);
    },
    [onChange, supplierData]
  );

  const handleClearSuppliers = useCallback(() => {
    setSupplierFilter(undefined);
    onChange([]);
  }, [onChange]);

  return (
    <GenericCard border={'light'} className={classNames('p-sm flex flex-col gap-xs', className)}>
      <Row className={'items-center justify-between'}>
        <span>Supplier</span>
        <Button variant={'link'} onClick={handleClearSuppliers}>
          <span className={'text-xs'}>Clear All {!!selectedCount && `(${selectedCount})`}</span>
        </Button>
      </Row>
      <SearchInput
        placeholder={'Filter suppliers'}
        value={supplierFilter ?? ''}
        onChange={handleChangeSupplierFilter}
        debounceTimeout={500}
      />
      <Col className={'gap-xs'}>
        {shownData.length ? (
          shownData.map(data => (
            <Row key={data.supplier.slug} className={'items-center gap-xs'}>
              <Checkbox
                id={data.supplier.slug}
                value={data.supplier.slug}
                checked={data.selected}
                onChange={handleChangeSelectedSuppliers}
              />
              <label htmlFor={data.supplier.slug} className={'whitespace-nowrap !font-normal grow'}>
                {data.supplier.name}
              </label>
            </Row>
          ))
        ) : (
          <span className={'text-center text-xs text-gray-200'}>No suppliers found</span>
        )}
        {filteredData.length > COLLAPSED_COUNT && (
          <Button className={'w-fit'} variant={'link'} onClick={toggleShowAll}>
            <span className={'text-xs'}>{showAll ? 'Show less' : 'Show more'}</span>
          </Button>
        )}
      </Col>
    </GenericCard>
  );
};

export default SearchBySupplierBox;
